/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Stack,
    Text,
    Heading,
    Button,
    AccordionIcon,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PromoCode, usePromoCode} from '../promo-code'
import {PostalCode, usePostalCode} from '../postal-code/index'
import PointsPreview from '../loyalty-coupon/points-preview'
import PromoCodeDisplay from '../../pages/checkout/partials/promo-code'
import CartItems from '../cart-items'
import FocusedAccordion from '../focused-accordion'
import {useCheckout} from '../../pages/checkout/util/checkout-context'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'
import {getIsFixedPriceModel} from '../../esw/esw-helpers'

const renderOrderSummaryContent = ({
    showHeading,
    isCheckout,
    isOrderConfirmation,
    basket,
    fontSize,
    savings,
    shippingItem,
    shipment,
    showPostalCodeForm,
    newPostalCode,
    handlePostalCodeChange,
    isPostalCodeInputVisible,
    setPostalCodeInputVisible,
    postalCodeProps,
    showPoints,
    showCartItems,
    checkoutObject,
    productsDiscountTotal,
    isFixedPriceModel = false,
    shouldHideLoyaltyUI,
    isInternational = false,
    ...otherProps
}) => {
    const shippingTotalValue =
        Number(
            basket?.c_eswShopperCurrencyDeliveryPriceInfo ??
                basket?.shippingTotal ??
                0
        ) || 0
    const currency = basket?.c_eswShopperCurrencyCode ?? basket?.currency
    const giftCardTotal =
        otherProps?.page === 'cart'
            ? 0
            : (basket.paymentInstruments || [])
                  .filter((instrument) => instrument.paymentMethodId === 'SMARTCLIXX')
                  .reduce((sum, instrument) => sum + (instrument.amount || 0), 0)
    const orderTotalValue = isInternational
        ? Number(
              basket?.c_eswShopperCurrencyPaymentAmount ??
                  basket?.orderTotal ??
                  0
          ) - giftCardTotal
        : (basket?.orderTotal || basket?.productTotal) - giftCardTotal

    const loyaltyCoupon = basket?.couponItems?.filter(
        (ci) => ci?.couponItemId === basket?.c_sflmLoyaltyCouponID
    )
    const loyaltyCouponCode = loyaltyCoupon && loyaltyCoupon.length ? loyaltyCoupon[0].code : ''

    const productSubtotalWithVAT = basket?.productSubTotal
    const productSubtotalExclVAT = basket?.productSubTotal - basket?.merchandizeTotalTax

    return (
        <>
            {showHeading && (
                <Heading fontSize="xl" pt={1} id="order-summary-heading" textTransform="uppercase">
                    <FormattedMessage
                        defaultMessage="Order Summary"
                        id="order_summary.heading.order_summary"
                    />
                </Heading>
            )}

            {isCheckout && (
                <PromoCodeDisplay
                    priceAdjustments={basket?.orderPriceAdjustments}
                    loyaltyCouponCode={loyaltyCouponCode}
                />
            )}

            <Stack spacing={5} align="flex-start" aria-labelledby="order-summary-heading">
                <Stack w="full" gap={0}>
                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text fontSize={fontSize}>
                            <FormattedMessage
                                defaultMessage="Subtotal (incl. VAT)"
                                id="order_summary.label.subtotal_including_vat"
                            />
                        </Text>
                        <Text fontSize={fontSize}>
                            <FormattedNumber
                                style="currency"
                                currency={basket?.currency}
                                value={productSubtotalWithVAT}
                            />
                        </Text>
                    </Flex>

                    {savings && (
                        <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                            <Text fontSize={fontSize}>
                                <FormattedMessage
                                    defaultMessage="Savings"
                                    id="order_summary.label.savings"
                                />
                            </Text>
                            <Text fontSize={fontSize}>
                                {/* TODO: change value to Savings */}
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={basket?.productSubTotal}
                                />
                            </Text>
                        </Flex>
                    )}
                    {productsDiscountTotal < 0 && (
                        <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                            <Text fontSize={fontSize}>
                                <FormattedMessage
                                    defaultMessage="Savings"
                                    id="order_summary.label.savings"
                                />
                            </Text>
                            <Text fontSize={fontSize}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={productsDiscountTotal}
                                />
                            </Text>
                        </Flex>
                    )}

                    {giftCardTotal > 0 && (
                        <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                            <Text fontSize={fontSize}>
                                <FormattedMessage
                                    defaultMessage="Gift Card"
                                    id="order_summary.label.gift_card"
                                />
                            </Text>

                            <Text fontSize={fontSize}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={-giftCardTotal}
                                />
                            </Text>
                        </Flex>
                    )}

                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Flex alignItems="center">
                            <Text lineHeight={1} fontSize={fontSize}>
                                {isCheckout ? (
                                    <FormattedMessage
                                        defaultMessage="Shipping"
                                        id="order_summary.label.shipping"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Estimated Shipping"
                                        id="order_summary.label.estimated_shipping"
                                    />
                                )}
                            </Text>
                        </Flex>

                        {shippingItem?.priceAdjustments?.some(
                            ({appliedDiscount}) => appliedDiscount?.type === 'free'
                        ) ? (
                            <Text as="span" fontSize={fontSize}>
                                <FormattedMessage
                                    defaultMessage="Free for you!"
                                    id="order_summary.label.free"
                                />
                            </Text>
                        ) : (
                            !isOrderConfirmation && isInternational ? (// for international, we only get shipping costs on order confirmation
                                <Text fontSize={fontSize}>
                                    <FormattedMessage
                                        defaultMessage="TBD"
                                        id="order_summary.label.TBD"
                                    />
                                </Text>
                            ) : (
                                <Text fontSize={fontSize}>
                                    <FormattedNumber
                                        value={shippingTotalValue}
                                        style="currency"
                                        currency={basket.currency}
                                    />
                                </Text>
                            )
                        )}
                    </Flex>

                    {showPostalCodeForm && (
                        <Box color="gray.500" maxWidth="220px">
                            <Text fontSize="xs">
                                <FormattedMessage
                                    defaultMessage="Estimation based on postal code {postalCode}."
                                    values={{
                                        postalCode: (
                                            <Text as="span" data-private>
                                                {newPostalCode}
                                            </Text>
                                        )
                                    }}
                                    id="order_summary.estimation.postal_code_text"
                                />
                            </Text>
                            <Flex direction="column" align="flex-start">
                                <Button
                                    variant="link"
                                    size={fontSize}
                                    onClick={handlePostalCodeChange}
                                    color="gray.500"
                                    fontSize="xs"
                                    textDecoration="underline"
                                >
                                    <FormattedMessage
                                        defaultMessage="Change postal code"
                                        id="order_summary.action.change_postal_code"
                                    />
                                </Button>
                                {isPostalCodeInputVisible && (
                                    <PostalCode
                                        {...postalCodeProps}
                                        onClose={() => setPostalCodeInputVisible(false)}
                                    />
                                )}
                            </Flex>
                        </Box>
                    )}

                    <Flex
                        w="full"
                        justify="space-between"
                        aria-live="polite"
                        aria-atomic="true"
                        mt="16px"
                    >
                        <Text fontWeight="bold" fontSize={fontSize} textTransform="uppercase">
                            {isCheckout ? (
                                <FormattedMessage
                                    defaultMessage="Your Total"
                                    id="order_summary.label.your_total"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            )}
                        </Text>
                        <Text fontWeight="bold" fontSize={fontSize} textTransform="uppercase">
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={orderTotalValue}
                            />
                        </Text>
                    </Flex>

                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text fontSize={fontSize}>
                            {isInternational ? (
                                <FormattedMessage
                                    defaultMessage="Includes duties and tax"
                                    id="order_summary.label.taxes_and_fees"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Includes VAT (20%)"
                                    id="order_summary.label.vat"
                                />
                            )}
                        </Text>
                    </Flex>
                </Stack>

                <Stack spacing={5} w="full">
                    {/* TODO: update the condition (whether points preview should be displayed) and points can be earned */}
                    {showPoints && !shouldHideLoyaltyUI && basket?.exportStatus === 'ready' && (
                        <Flex justify="center" bg="gray.200" py="2" px="4">
                            <Text
                                color="darkPink"
                                fontWeight="500"
                                textAlign="center"
                                fontSize="14px"
                                lineHeight="18px"
                            >
                                <FormattedMessage
                                    defaultMessage="It's so close! You'll earn {points} points once your order is shipped!"
                                    id="checkout_confirmation.points_earned_registered"
                                    values={{
                                        points: (
                                            <Text as="span" data-private>
                                                {basket.c_loyaltyV2PointsEarned}
                                            </Text>
                                        )
                                    }}
                                />
                            </Text>
                        </Flex>
                    )}
                    {basket?.c_loyaltyV2PointsEarned &&
                        !shouldHideLoyaltyUI &&
                        !isOrderConfirmation && (
                            <PointsPreview
                                points={basket?.c_loyaltyV2PointsEarned}
                                isCheckout={isCheckout}
                            />
                        )}
                    {!isCheckout && !isFixedPriceModel && (
                        <Box w="full">
                            <PromoCode basket={basket} />
                        </Box>
                    )}
                </Stack>

                {showCartItems && <CartItems basket={basket} />}
            </Stack>
        </>
    )
}

const OrderSummary = ({
    basket,
    totalItems,
    showPostalCodeForm = false,
    showCartItems = false,
    isEstimate = false,
    fontSize = 'sm',
    isCheckout = false,
    isOrderConfirmation = false,
    savings,
    showHeading = true,
    showPoints = true,
    isMobileAccordion = false,
    ...otherProps
}) => {
    const checkoutObject = useCheckout()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {...postalCodeProps} = usePostalCode()
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const isInternational = Boolean(basket?.c_eswShopperCurrencyPaymentAmount || basket?.c_eswOrderNo)

    const [isPostalCodeInputVisible, setPostalCodeInputVisible] = useState(false)
    const [newPostalCode, setNewPostalCode] = useState('')
    const [productsDiscountTotal, setProductsDiscountTotal] = useState(0)
    const [isFixedPriceModel, setIsFixedPriceModel] = useState(false)

    useEffect(() => {
        setIsFixedPriceModel(getIsFixedPriceModel())
    }, [])

    useEffect(() => {
        if (basket?.c_productsDiscountTotal !== undefined) {
            setProductsDiscountTotal(basket.c_productsDiscountTotal)
        }
    }, [basket?.c_productsDiscountTotal])

    const handlePostalCodeChange = () => {
        setPostalCodeInputVisible(true)
    }

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    const shippingItem = basket.shippingItems?.[0]
    const shipment = basket.shipments.find((shipment) => shipment.shipmentId === 'me')
    const postalCode = shipment?.shippingAddress?.postalCode

    if (postalCode && postalCode !== newPostalCode) {
        setNewPostalCode(postalCode)
    }

    const currency = basket?.c_eswShopperCurrencyCode ?? basket?.currency
    const mobileOrderTotalValue = isInternational
        ? Number(
              basket?.c_eswShopperCurrencyPaymentAmount ??
                  basket?.orderTotal ??
                  basket?.productTotal ??
                  0
          )
        : basket?.orderTotal || basket?.productTotal

    return (
        <Stack
            data-testid="sf-order-summary"
            position="relative"
            spacing={5}
            background="white"
            zIndex={1}
            mx={isMobileAccordion ? 4 : 0}
            mt={isMobileAccordion ? '18px' : 0}
        >
            {isMobileAccordion && !isDesktop && (
                <FocusedAccordion
                    AccordionButtonContent={
                        <Flex alignItems="center" width="100%" columnGap={2}>
                            <Text
                                fontWeight="bold"
                                fontSize={fontSize}
                                textTransform="uppercase"
                                flexGrow={1}
                                textAlign="left"
                            >
                                Your total ({totalItems})
                            </Text>
                            <Text fontWeight="bold" fontSize={fontSize} textTransform="uppercase">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency}
                                    value={mobileOrderTotalValue}
                                />
                            </Text>
                            <AccordionIcon />
                        </Flex>
                    }
                    AccordionPanelContent={
                        <Stack spacing={5} pb={4}>
                            {renderOrderSummaryContent({
                                showHeading,
                                isCheckout,
                                isOrderConfirmation,
                                basket,
                                fontSize,
                                savings,
                                shippingItem,
                                shipment,
                                showPostalCodeForm,
                                newPostalCode,
                                handlePostalCodeChange,
                                isPostalCodeInputVisible,
                                setPostalCodeInputVisible,
                                postalCodeProps,
                                showPoints,
                                showCartItems,
                                isEstimate,
                                checkoutObject,
                                productsDiscountTotal,
                                isFixedPriceModel,
                                shouldHideLoyaltyUI,
                                isInternational,
                                ...otherProps
                            })}
                        </Stack>
                    }
                />
            )}
            {(!isCheckout || !isMobileAccordion) &&
                renderOrderSummaryContent({
                    showHeading,
                    isCheckout,
                    isOrderConfirmation,
                    basket,
                    fontSize,
                    savings,
                    shippingItem,
                    shipment,
                    showPostalCodeForm,
                    newPostalCode,
                    handlePostalCodeChange,
                    isPostalCodeInputVisible,
                    setPostalCodeInputVisible,
                    postalCodeProps,
                    showPoints,
                    showCartItems,
                    isEstimate,
                    checkoutObject,
                    productsDiscountTotal,
                    isFixedPriceModel,
                    shouldHideLoyaltyUI,
                    isInternational,
                    ...otherProps
                })}
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    totalItems: PropTypes.number,
    showPostalCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    savings: PropTypes.number,
    isCheckout: PropTypes.bool,
    isOrderConfirmation: PropTypes.bool,
    showPoints: PropTypes.bool,
    showHeading: PropTypes.bool,
    isMobileAccordion: PropTypes.bool
}

export default OrderSummary
